import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '../../shared/hooks/useLocalStorage';
import { ActivityView } from './activityViewSelector';

/** localStorage key for the per-device sticky view choice (plan 0017 §1). */
const STORAGE_KEY = 'activity.view';
/** URL query param for the `?view=` deep link. */
const PARAM_KEY = 'view';
const DEFAULT_VIEW: ActivityView = 'list';

const isActivityView = (v: string | null | undefined): v is ActivityView =>
  v === 'list' || v === 'calendar';

/**
 * Activity list ↔ calendar view state with persistence + deep linking
 * (plan 0017 §1 "Persistence", P2.S3). Precedence:
 *
 *  1. A valid `?view=` URL param wins on load (deep link / shared URL).
 *  2. Otherwise the value persisted in localStorage (per-device, sticky).
 *  3. Otherwise the default (`list`).
 *
 * **localStorage only** — there is NO server-side user-preference write
 * (decision locked 2026-06-25). Setting the view writes BOTH the URL (so the
 * link is shareable / reload-stable) and localStorage (so a no-param reload
 * restores it), and the URL update is additive — other query params survive.
 *
 * The URL is the live source of truth once a param is present: on mount, if the
 * URL has no (valid) `view` param, we seed it from the stored value so the
 * resolved view and the URL agree without an extra render of the wrong view.
 */
export const useActivityView = (): [ActivityView, (view: ActivityView) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedView, setStoredView] = useLocalStorage(STORAGE_KEY, DEFAULT_VIEW);

  const paramView = searchParams.get(PARAM_KEY);
  const storedSafe: ActivityView = isActivityView(storedView)
    ? storedView
    : DEFAULT_VIEW;

  // URL param wins when valid; otherwise fall back to the stored value.
  const view: ActivityView = isActivityView(paramView) ? paramView : storedSafe;

  // Seed the URL from the stored value when it has no valid `view` param, so a
  // bare `/activities` load reflects the sticky choice in the URL too. Additive:
  // preserves every other query param. `replace` keeps it out of history.
  useEffect(() => {
    if (!isActivityView(paramView)) {
      setSearchParams(
        (prev) => {
          prev.set(PARAM_KEY, storedSafe);
          return prev;
        },
        { replace: true }
      );
    }
    // Only on the param transitioning to invalid/absent — storedSafe is stable
    // enough (it only changes via setView, which also sets the param).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramView]);

  const setView = useCallback(
    (next: ActivityView) => {
      setStoredView(next);
      setSearchParams(
        (prev) => {
          prev.set(PARAM_KEY, next);
          return prev;
        },
        { replace: true }
      );
    },
    [setStoredView, setSearchParams]
  );

  return [view, setView];
};
